import React, { useState, useEffect } from 'react';
import Header from './components/Header';
import PlayerList from './components/PlayerList';
import SeasonSelector, { SeasonOption } from './components/SeasonSelector';
import { Player, SeasonApiResponse, SeasonStats } from './types';
import { api } from './services/api';
import './App.css';
import PlayerStatsModal from './components/PlayerStatsModal';
function App() {
  const [seasonsData, setSeasonsData] = useState<SeasonApiResponse | null>(null);
  const [selectedSeason, setSelectedSeason] = useState<string>('');
  const [players, setPlayers] = useState<Player[]>([]);
  const [stats, setStats] = useState<SeasonStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Modal state
  const [selectedPlayer, setSelectedPlayer] = useState<Player | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Helper function to get season display name
  const getSeasonDisplayName = (seasonKey: string): string => {
    // Extract year from season key (e.g., "karpatstars2425" -> "2024-2025")
    if (seasonKey.startsWith('karpatstars')) {
      const yearPart = seasonKey.replace('karpatstars', '');
      if (yearPart.length === 4) {
        const firstYear = '20' + yearPart.substring(0, 2);
        const secondYear = '20' + yearPart.substring(2, 4);
        return `${firstYear}-${secondYear}`;
      }
    }
    return seasonKey;
  };

  // Helper function to get available seasons
  const getAvailableSeasons = (): SeasonOption[] => {
    if (!seasonsData) return [];
    
    // Get all season keys and sort them (newest first)
    const seasonKeys = Object.keys(seasonsData).filter(key => key !== 'karpatstars'); // Filter out empty karpatstars key
    const sortedSeasonKeys = seasonKeys.sort((a, b) => {
      // Sort by season key in descending order (newest first)
      return b.localeCompare(a);
    });
    
    return sortedSeasonKeys.map(seasonKey => ({
      key: seasonKey,
      label: `Kausi ${getSeasonDisplayName(seasonKey)}`,
      hasData: seasonsData[seasonKey] && seasonsData[seasonKey].length > 0
    }));
  };

  // Update players when season changes
  useEffect(() => {
    if (seasonsData && selectedSeason) {
      const seasonPlayers = seasonsData[selectedSeason as keyof SeasonApiResponse];
      setPlayers(seasonPlayers || []);
    }
  }, [seasonsData, selectedSeason]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        const [seasonsData, statsData] = await Promise.all([
          api.getSeasons(),
          api.getStats()
        ]);
        
        setSeasonsData(seasonsData);
        setStats(statsData);
        
        // Set default season to first available season if none selected
        if (!selectedSeason) {
          const availableSeasons = Object.keys(seasonsData).filter(key => 
            key !== 'karpatstars' && seasonsData[key] && seasonsData[key].length > 0
          );
          if (availableSeasons.length > 0) {
            const sortedSeasons = availableSeasons.sort((a, b) => b.localeCompare(a));
            setSelectedSeason(sortedSeasons[0]);
          }
        }
        
        // Set initial players for selected season
        const currentSeason = selectedSeason || Object.keys(seasonsData).filter(key => 
          key !== 'karpatstars' && seasonsData[key] && seasonsData[key].length > 0
        ).sort((a, b) => b.localeCompare(a))[0];
        
        if (currentSeason) {
          const initialPlayers = seasonsData[currentSeason];
          setPlayers(initialPlayers || []);
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Virhe tietojen lataamisessa. Yritä uudelleen.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [selectedSeason]);

  const handleRefresh = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const [seasonsData, statsData] = await Promise.all([
        api.getSeasons(),
        api.getStats()
      ]);
      
      setSeasonsData(seasonsData);
      setStats(statsData);
      
      // Update players for current season
      if (selectedSeason && seasonsData[selectedSeason]) {
        const currentPlayers = seasonsData[selectedSeason];
        setPlayers(currentPlayers || []);
      } else {
        // If no season selected, set to first available
        const availableSeasons = Object.keys(seasonsData).filter(key => 
          key !== 'karpatstars' && seasonsData[key] && seasonsData[key].length > 0
        );
        if (availableSeasons.length > 0) {
          const sortedSeasons = availableSeasons.sort((a, b) => b.localeCompare(a));
          setSelectedSeason(sortedSeasons[0]);
          setPlayers(seasonsData[sortedSeasons[0]] || []);
        }
      }
    } catch (err) {
      console.error('Error refreshing data:', err);
      setError('Virhe tietojen päivittämisessä. Yritä uudelleen.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSeasonChange = (seasonKey: string) => {
    setSelectedSeason(seasonKey);
  };

  // Modal handlers
  const handlePlayerClick = (player: Player) => {
    setSelectedPlayer(player);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedPlayer(null);
  };

  if (error) {
    return (
      <div className="app">
        <Header stats={null} isLoading={false} />
        <div className="error-container">
          <div className="error-message">
            <h2>Virhe</h2>
            <p>{error}</p>
            <button onClick={handleRefresh} className="refresh-button">
              Yritä uudelleen
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      <Header stats={stats ? stats[selectedSeason] : null} isLoading={isLoading} />
      
      <main className="main-content">
        <div className="container">
          {isLoading ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Ladataan pelaajia...</p>
            </div>
          ) : !seasonsData ? (
            <div className="empty-state">
              <div className="empty-icon">🏒</div>
              <h2>Ei tietoja vielä</h2>
              <p>Kausien tietoja ei ole vielä saatavilla. Tarkista myöhemmin uudelleen.</p>
              <button onClick={handleRefresh} className="refresh-button">
                Päivitä
              </button>
            </div>
          ) : (
            <>
              <SeasonSelector
                seasons={getAvailableSeasons()}
                selectedSeason={selectedSeason}
                onSeasonChange={handleSeasonChange}
              />
              {players.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">🏒</div>
                  <h2>Ei pelaajia tällä kaudella</h2>
                  <p>Valitun kauden pelaajien tietoja ei ole vielä saatavilla.</p>
                  <button onClick={handleRefresh} className="refresh-button">
                    Päivitä
                  </button>
                </div>
              ) : (
                <PlayerList 
                  players={players} 
                  seasonName={getSeasonDisplayName(selectedSeason)}
                  currentRound={stats && stats[selectedSeason] ? stats[selectedSeason].currentRound : undefined}
                  onPlayerClick={handlePlayerClick}
                />
              )}
            </>
          )}
        </div>
      </main>
      
      {/* Modal rendered at app level for proper positioning */}
      <PlayerStatsModal
        player={selectedPlayer}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />
    </div>
  );
}

export default App;
